import React from "react";
import { Link } from "react-router-dom";
import { Facebook, Twitter, Instagram, Linkedin, Youtube, Mail, Phone, MapPin, ShieldCheck, Send, MessageCircle } from "lucide-react";
import { CafLogo } from "@/components/CafLogo";
import { NewsletterForm } from "@/components/forms/NewsletterForm";
import { useSite } from "@/lib/site";

const SECTIONS = [
  {
    title: "Programs",
    links: [
      ["/programs", "All Programs"], ["/programs/disaster-relief", "Disaster Relief"], ["/programs/education-support", "Education"],
      ["/programs/medical-assistance", "Medical"], ["/programs/environmental-protection", "Environment"], ["/programs/womens-empowerment", "Women's Empowerment"],
    ],
  },
  {
    title: "Awareness",
    links: [
      ["/awareness", "Awareness Hub"], ["/awareness/know-your-rights", "Know Your Rights"], ["/awareness/consumer", "Consumer"],
      ["/awareness/legal", "Legal"], ["/awareness/digital-safety", "Digital Safety"], ["/awareness/environmental", "Environmental"],
    ],
  },
  {
    title: "Engage",
    links: [
      ["/donate", "Donate"], ["/volunteer", "Volunteer"], ["/membership", "Digital Membership"],
      ["/training", "Training"], ["/report-issue", "Report Issue"], ["/youth-voice", "Youth Voice"],
    ],
  },
  {
    title: "Trust",
    links: [
      ["/transparency", "Transparency"], ["/governance", "Governance"], ["/annual-reports", "Annual Reports"],
      ["/csr", "CSR"], ["/grievance-redressal", "Grievance Redressal"], ["/code-of-ethics", "Code of Ethics"],
    ],
  },
];

const POLICIES = [
  ["/privacy", "Privacy"], ["/terms", "Terms"], ["/refund-policy", "Refund"], ["/donation-policy", "Donation"],
  ["/accessibility", "Accessibility"], ["/child-protection", "Child Protection"], ["/volunteer-policy", "Volunteer Policy"],
  ["/conflict-of-interest", "Conflict of Interest"],
];

export const Footer = () => {
  const site = useSite();
  return (
    <footer data-testid="site-footer" className="relative mt-20 bg-black text-white overflow-hidden">
      <div className="absolute inset-x-0 top-0 h-0.5 tricolour-strip-h" aria-hidden="true" />
      <div className="absolute inset-0 grain opacity-70" aria-hidden="true" />
      <div className="container-wide relative py-14">
        <div className="grid gap-10 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <CafLogo variant="light" />
            <p className="mt-4 text-white/75 text-sm leading-relaxed max-w-sm">{site.footer_about}</p>
            <div className="mt-5 flex items-center gap-2">
              {[
                [site.facebook_url, "Facebook", Facebook],
                [site.twitter_url, "X (Twitter)", Twitter],
                [site.instagram_url, "Instagram", Instagram],
                [site.linkedin_url, "LinkedIn", Linkedin],
                [site.youtube_url, "YouTube", Youtube],
                [site.whatsapp_url, "WhatsApp", MessageCircle],
                [site.telegram_url, "Telegram", Send],
              ].filter(([href]) => href && href.trim()).map(([href, label, Icon]) => (
                <a key={label} href={href} target="_blank" rel="noopener noreferrer" aria-label={label}
                  data-testid={`footer-social-${label.toLowerCase().replace(/[^a-z]/g, "")}`}
                  className="h-9 w-9 grid place-items-center rounded-full border border-white/15 hover:border-[hsl(var(--orange))] hover:text-[hsl(var(--orange))] transition-colors">
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>
            <div className="mt-6">
              <h4 className="text-xs font-semibold uppercase tracking-[0.14em] text-white/70">Newsletter</h4>
              <p className="mt-2 text-xs text-white/70">Monthly impact updates. No spam.</p>
              <div className="mt-3"><NewsletterForm variant="dark" /></div>
            </div>
          </div>
          <div className="lg:col-span-8 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {SECTIONS.map((s) => (
              <div key={s.title}>
                <h4 className="text-xs font-semibold uppercase tracking-[0.14em] text-white/70">{s.title}</h4>
                <ul className="mt-3 space-y-2 text-sm">
                  {s.links.map(([to, label]) => (
                    <li key={to}><Link to={to} className="text-white/85 hover:text-[hsl(var(--orange))]">{label}</Link></li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-10 pt-6 border-t border-white/10 grid gap-4 md:grid-cols-2">
          <div className="space-y-1.5 text-sm text-white/80">
            <div className="flex items-center gap-2"><Mail className="h-4 w-4" /> {site.contact_email}</div>
            <div className="flex items-center gap-2"><Phone className="h-4 w-4" /> {site.contact_phone}</div>
            <div className="flex items-start gap-2"><MapPin className="h-4 w-4 mt-0.5" /> {site.address}</div>
          </div>
          <div className="flex items-center md:justify-end gap-2 text-xs text-white/70">
            <ShieldCheck className="h-4 w-4 text-[hsl(var(--orange))]" />
            <span>{site.registered_line}</span>
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs text-white/60">
          <div>© {new Date().getFullYear()} {site.org_name}. All rights reserved.</div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            {POLICIES.map(([to, label]) => (<Link key={to} to={to} className="hover:text-[hsl(var(--orange))]">{label}</Link>))}
          </div>
        </div>
      </div>
    </footer>
  );
};
